import React from 'react';
import { FormattedMessage } from 'react-intl';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';

import { DatePicker } from 'ui-components/components/date-picker';
import { Text, TextType } from 'ui-components/components/text/index';
import { noop } from 'ui-components/default-props/noop';
import { FlexColumn } from 'ui-components/components/flex';
import { LabelWithRequiredIndicator } from 'ui-components/components/label-with-required-indicator';
import Global from '../../../store/global-selectors';

import { DateService } from '../../../services/date-service';
import { messages } from '../../modal-components/pickup-window/messages';

const sundayMomentDayIndex = 0;
const getIsDayDisabledFromSelectionChecker = (timeZoneIana, minimumDaysInAdvance, shouldDisableSundayPickups) => {
  // NOTE: the underlying datepicker uses moment
  return (day) => {
    const isSunday = day.day() === sundayMomentDayIndex;
    if (shouldDisableSundayPickups && isSunday) {
      return true;
    }

    const datePickerDay = DateTime.fromObject(
      {
        year: day.year(),
        month: day.month() + 1, // moment months are 0-indexed, luxon months are 1-indexed
        day: day.date(),
      },
      { zone: timeZoneIana },
    );

    const nowInWarehouse = DateTime.utc().setZone(timeZoneIana);
    const earliestPickupDate = nowInWarehouse.plus({ days: minimumDaysInAdvance });

    const isDatePickerDayBeforeEarliestAvailablePickupDate = datePickerDay.startOf('day') < earliestPickupDate.startOf('day');
    return isDatePickerDayBeforeEarliestAvailablePickupDate;
  };
};

/**
 * PickupDate component for selecting pickup date
 * @param {object} props
 * @param {number} [props.minimumDaysInAdvance] - Minimum days in advance for date selection
 * @param {string} [props.pickupDate] - The selected pickup date
 * @param {string} [props.timeZoneIana] - IANA timezone identifier
 * @param {(date: string) => void} [props.setPickupDate] - Callback when date is changed
 * @param {boolean} [props.shouldDisableSundayPickups] - When true, disables Sundays in the date picker
 * @returns {React.ReactElement} The PickupDate component
 */
export const PickupDate = ({
  minimumDaysInAdvance = 0,
  pickupDate = '',
  timeZoneIana,
  setPickupDate = noop,
  shouldDisableSundayPickups = false,
}) => {
  const nonSelectableDateChecker = getIsDayDisabledFromSelectionChecker(timeZoneIana, minimumDaysInAdvance, shouldDisableSundayPickups);
  const dateFormat = useSelector(Global.selectUserUserDateFormat) || DateService.getDefaultDateFormat();
  return (
    <FlexColumn>
      <LabelWithRequiredIndicator>
        <Text textType={TextType.Label} value={<FormattedMessage {...messages.pickupDate} />} />
      </LabelWithRequiredIndicator>
      <DatePicker
        dateFormat={dateFormat}
        isOutsideRange={nonSelectableDateChecker}
        onChange={setPickupDate}
        shouldInputHaveFullWidth
        value={pickupDate}
      />
    </FlexColumn>
  );
};
